import {
  ETextParamType_CancelToCopyVideoFrameToSystem,
  ETextParamType_CopyVideoFrameToSystem,
  HEL_VIDEO_PLAYER_DEFAULT,
  HEL_VIDEO_PLAYER_IVS,
  HEL_VIDEO_PLAYER_HLS,
  ETextParamType_IsPlayVideo
} from './logics/const';
import {
  getSceneJsonPath,
  getVideoAudioContext,
  getVideoEndedToggle,
  getVideoGainNode,
  getVideoHlsConfig,
  getVideoSource,
  getVideoInitialized,
  setPendingAutoPlayRequest,
  setSceneJson,
  setVideoAudioContext,
  setVideoGainNode,
  setVideoHlsConfig,
  setVideoInfo,
  setVideoSource,
  setVideoSourceUrl,
  getVideoMediastreamSrc
} from './logics/dataOps';
import {getVideo} from './logics/domOps';
import {hel_setText} from './logics/moduleCaller';
import {hel_isiPhone, hel_isMac} from './logics/platform';
import {
  hel_video_close_genericwindow,
  hel_video_is_paused,
  hel_video_open_genericwindow,
  hel_video_stop,
  helcpp_video_Prepare,
  helcore_video_play,
  helcore_video_play_mediastream,
  helcore_video_syncplay_clear_basetime,
  helcore_video_syncplay_set_basetime,
  helcore_video_syncplay_adjust_currenttime,
  setVideoPlayInfo,
  setCurrentVideoPlayTarget,
  getCurrentVideoPlayTarget,
  videoPlayErrorHandler
} from './logics/video_callee';

import type IVSPlayerType from 'amazon-ivs-player';
import type HlsType from 'hls.js';
import type {HlsConfig} from 'hls.js';

declare global {
  /* eslint-disable no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須 */
  /**
   * IVSライブを使う案件において、main.htmlで別に読み込まれる。
   * @see https://github.com/VRHIKKY/VketCloud_LiveResearch/blob/fd07e5779334079bdd6fa4d8e8a3e380199d2b8c/musv4_leet_ivsp/main.html#L145
   * @see https://discord.com/channels/596169759045976094/1243065534916853930/1331132832294109274
   */
  var IVSPlayer: typeof IVSPlayerType | undefined;
  var Hls: typeof HlsType;
  var hel_video_on_loadedmetadata: (() => void) | undefined;
  /* eslint-enable */
}

/**
 * @remarks
 * このファイル内でのみ呼出し
 */
const helcore_video_node_gain = () => {
  if (!hel_isiPhone() && !hel_isMac()) {
    // iOS/Mac以外ではVideo.volumeが使えるのでgainNodeは生成しない
    return;
  }

  if (getVideoAudioContext() === undefined) {
    setVideoAudioContext(new (window.AudioContext ?? window.webkitAudioContext)());
  }

  let videoSource = getVideoSource();
  const audioContext = getVideoAudioContext();
  const video = getVideo();
  if (videoSource === undefined && audioContext !== undefined && video !== undefined) {
    setVideoSource(audioContext.createMediaElementSource(video));
    videoSource = getVideoSource();
  }

  const videoGainNode = getVideoGainNode();
  if (videoGainNode === undefined && videoSource !== undefined && audioContext !== undefined) {
    const created = audioContext.createGain();
    setVideoGainNode(created);
    videoSource.connect(created);
    created.connect(audioContext.destination);
  }
};

Object.assign(globalThis, {
  /**
   * @remarks
   * C++から呼出し
   * @param uint8Array
   * @param x
   * @param y
   * @param width
   * @param height
   */
  g_on_video_frame_capture: (
    uint8Array: Uint8Array,
    x: number,
    y: number,
    width: number,
    height: number
  ) => {
    console.debug('g_on_video_frame_capture:', uint8Array, x, y, width, height);
  },

  // Video

  /**
   * @remarks
   * 公開関数
   * @param itemName
   * @param materialName
   * @param fileName
   * @param loop
   * @param streaming
   * @param playSyncBaseTime
   */
  hel_video_play: (
    itemName: string,
    materialName: string,
    src: string | MediaStream,
    loop: boolean,
    streaming: boolean,
    playSyncBaseTime: number | undefined
  ) => {
    if (typeof src === 'string') {
      helcore_video_play(
        itemName,
        materialName,
        src,
        loop,
        streaming,
        true,
        playSyncBaseTime ?? null
      );
    } else {
      helcore_video_play_mediastream(
        itemName,
        materialName,
        src,
        loop,
        streaming,
        true,
        playSyncBaseTime ?? null
      );
    }
  },

  /**
   * @remarks
   * C++から呼出し
   * @param videoSrc
   * @param videoStreaming
   * @param playSyncBaseTime = unixtimestamp(ミリ秒) 2024/4/10 20:24:53 -> 1712748293437
   */
  helcpp_video_play: (
    videoSrc: string,
    videoStreaming: 0 | 1,
    playSyncBaseTime: number | undefined | null
  ): void => {
    if (typeof videoSrc === 'string') {
      setVideoSourceUrl(videoSrc);
    }
    const video = getVideo();
    if (video === undefined) {
      return;
    }
    const currentVidePlayTarget = getCurrentVideoPlayTarget();
    const loop = video.loop;
    const videoPlayInfo = {
      itemName: currentVidePlayTarget?.itemName ?? '',
      materialName: currentVidePlayTarget?.materialName ?? '',
      src: videoSrc,
      loop,
      streaming: !!videoStreaming,
      playSyncBaseTime: playSyncBaseTime ?? null
    };
    setVideoPlayInfo(videoPlayInfo);
    // 動画の時刻同期
    helcore_video_syncplay_clear_basetime();
    if (playSyncBaseTime) {
      helcore_video_syncplay_set_basetime(playSyncBaseTime);
      const onLoadMetaDataSyncPlay = () => {
        helcore_video_syncplay_adjust_currenttime(video, playSyncBaseTime);
      };
      const onErrorSyncPlayRemove = () => {
        video.removeEventListener('loadedmetadata', onLoadMetaDataSyncPlay);
        video.removeEventListener('canplay', onCanPlayRemoveErrorHandler);
      };
      const onCanPlayRemoveErrorHandler = () => {
        video.removeEventListener('error', onErrorSyncPlayRemove);
      };
      video.addEventListener('loadedmetadata', onLoadMetaDataSyncPlay, {once: true});
      video.addEventListener('error', onErrorSyncPlayRemove, {once: true});
      video.addEventListener('canplay', onCanPlayRemoveErrorHandler, {once: true});
    }

    if (videoSrc.includes('__MEDIASTREAM__')) {
      globalThis.g_VideoPlayerType = HEL_VIDEO_PLAYER_DEFAULT;
      helcore_video_node_gain();
      const stream = getVideoMediastreamSrc();
      if (!stream) {
        console.error('video src error. invalid stream');
        return;
      }
      video.srcObject = stream;
      video.src = '';
      video.play().catch((e) => {
        videoPlayErrorHandler(e);
      });
    } else if (videoStreaming !== 0) {
      if (typeof IVSPlayer !== 'undefined' && IVSPlayer.isPlayerSupported) {
        globalThis.g_VideoPlayerType = HEL_VIDEO_PLAYER_IVS;
        helcore_video_node_gain();

        const script = document.querySelector('script[src$="/amazon-ivs-player.min.js"]');
        if (!script || !(script instanceof HTMLScriptElement)) {
          throw new Error('The <script> tag for loading amazon-ivs-player.min.js was not found.');
        }
        const player = IVSPlayer.create({
          wasmWorker: new URL('amazon-ivs-wasmworker.min.js', script.src).href,
          wasmBinary: new URL('amazon-ivs-wasmworker.min.wasm', script.src).href
        });
        player.attachHTMLVideoElement(video);
        player.load(videoSrc);
        player.play();
      } else if (!hel_isiPhone() && typeof Hls !== 'undefined' && Hls.isSupported()) {
        globalThis.g_VideoPlayerType = HEL_VIDEO_PLAYER_HLS;
        helcore_video_node_gain();

        // Create Hls
        globalThis.g_VideoHls = null;
        const hlsConfig = getVideoHlsConfig();
        if (hlsConfig !== undefined) {
          globalThis.g_VideoHls = new Hls(hlsConfig);
        } else {
          globalThis.g_VideoHls = new Hls();
        }

        //
        globalThis.g_VideoHls.loadSource(videoSrc);
        globalThis.g_VideoHls.attachMedia(video);
        video.play().catch((e) => {
          videoPlayErrorHandler(e);
        });
        globalThis.g_VideoHls.startLoad();
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        globalThis.g_VideoPlayerType = HEL_VIDEO_PLAYER_DEFAULT;
        helcore_video_node_gain();

        video.srcObject = null;
        video.src = videoSrc;
        video.play().catch((e) => {
          videoPlayErrorHandler(e);
        });
      } else {
        console.error('video streaming not supported');
      }
    } else {
      globalThis.g_VideoPlayerType = HEL_VIDEO_PLAYER_DEFAULT;
      helcore_video_node_gain();

      video.srcObject = null;
      video.src = videoSrc;
      video.play().catch((e) => {
        videoPlayErrorHandler(e);
      });
    }
  },

  /**
   * @remarks
   * 公開関数
   */
  hel_video_stop,
  helcpp_video_Prepare,

  /**
   * @remarks
   * C++から呼出し
   */
  helcpp_video_stop: () => {
    const video = getVideo();
    if (video === undefined) {
      return;
    }

    video.pause();
    if (globalThis.g_VideoPlayerType === HEL_VIDEO_PLAYER_HLS) {
      if (!globalThis.g_VideoHls) {
        throw new Error('g_VideoHls is not found.');
      }
      globalThis.g_VideoHls.stopLoad();
    }
    // setVideo(null);
    globalThis.g_VideoPlayerType = null;
  },

  /**
   * @remarks
   * C++から呼出し
   * @param pause_enable
   */
  helcpp_video_playpause_toggle: async (pause_enable: boolean) => {
    const video = getVideo();
    if (video === undefined) {
      return;
    }

    if (pause_enable) {
      video.pause();
      if (globalThis.g_VideoPlayerType === HEL_VIDEO_PLAYER_HLS) {
        if (!globalThis.g_VideoHls) {
          throw new Error('g_VideoHls is not found.');
        }
        globalThis.g_VideoHls.stopLoad();
      }
    } else {
      await video.play();
      if (globalThis.g_VideoPlayerType === HEL_VIDEO_PLAYER_HLS) {
        if (!globalThis.g_VideoHls) {
          throw new Error('g_VideoHls is not found.');
        }
        globalThis.g_VideoHls.startLoad();
      }
    }
  },

  /**
   * @remarks
   * C++とhel_video_set_movieviewer_uiから呼出し
   */
  hel_video_is_paused,

  /**
   * @remarks
   * C++から呼出し
   * @param seconds
   */
  hel_video_add_forward: (seconds: number) => {
    const video = getVideo();
    if (video === undefined) {
      return;
    }
    video.currentTime += seconds;
  },

  /**
   * @remarks
   * 公開関数
   */
  hel_video_isplay: () => {
    return hel_setText(ETextParamType_IsPlayVideo, '');
  },

  /**
   * @remarks
   * C++から呼出し
   */
  helcpp_video_isplay: () => {
    const video = getVideo();
    if (video === undefined) {
      return false;
    }

    return !(video.loop && getVideoEndedToggle());
  },

  /**
   * @remarks
   * C++から呼出し
   * @param seconds
   */
  hel_video_add_rewind: (seconds: number) => {
    const video = getVideo();
    if (video === undefined) {
      return;
    }
    video.currentTime -= seconds;
  },

  /**
   * @remarks
   * C++から呼出し
   */
  hel_video_get_duration: (): number => {
    const video = getVideo();
    if (video === undefined) {
      return 0;
    }
    return video.duration;
  },

  /**
   * @remarks
   * C++から呼出し
   * @param sliderRate
   */
  hel_video_set_currenttime: (sliderRate: number) => {
    const video = getVideo();
    if (video === undefined) {
      return;
    }
    if (video.duration > 0) {
      video.currentTime = sliderRate * video.duration;
    }
  },

  /**
   * @remarks
   * C++から呼出し
   */
  hel_video_get_currenttime: (): number => {
    const video = getVideo();
    if (video === undefined) {
      return 0;
    }
    return video.currentTime;
  },

  /**
   * @remarks
   * heliodor_front.js等から呼ばれるケースが多い
   */
  hel_video_SetHlsConfig: (config: HlsConfig) => setVideoHlsConfig(config),

  /**
   * @remarks
   * 公開関数
   * @param seconds
   */
  hel_video_seek: (seconds: number) => {
    const video = getVideo();
    if (video === undefined) {
      return;
    }
    video.currentTime = seconds;
  },

  /**
   * @remarks
   * heliodor_xrw_video等から呼び出しあり
   * @param mute
   */
  hel_video_SetMute: (mute: boolean) => {
    const video = getVideo();
    if (video === undefined) {
      return;
    }

    video.muted = mute;
  },

  /**
   * @remarks
   * JSから参照有り
   */
  hel_video_open_genericwindow,

  /**
   * @remarks
   * JSから参照有り
   */
  hel_video_close_genericwindow,

  /**
   * Cppから参照有り
   */
  helcpp_video_request_autoplay: (
    itemName: string,
    materialName: string,
    fileName: string,
    loop: boolean,
    streaming: boolean,
    seekbar: boolean,
    playSyncBaseTime: number | undefined
  ) => {
    if (getVideoInitialized()) {
      helcore_video_play(
        itemName,
        materialName,
        fileName,
        loop,
        streaming,
        seekbar,
        playSyncBaseTime ?? null
      );
    } else {
      setPendingAutoPlayRequest({
        itemName,
        materialName,
        src: fileName,
        loop,
        streaming,
        playSyncBaseTime: playSyncBaseTime ?? null
      });
    }
  },

  /**
   * Cppから参照有り
   */
  helcpp_video_set_current_target: (itemName: string, materialName: string) => {
    // Cppでビデオ再生直前に呼ばれる関数
    // これから再生するビデオのアイテム名とマテリアル名をTS層に通知する
    setCurrentVideoPlayTarget({itemName, materialName});
  }
});

/**
 * 未使用
 */
const hel_video_get_slider_rate = (): number => {
  const video = getVideo();
  if (video === undefined) {
    return 0.0;
  }

  let sliderRate = 0.0;
  if (video.duration > 0.0) {
    sliderRate = video.currentTime / video.duration;
  }
  return sliderRate;
};

/**
 * 未使用
 * @param x
 * @param y
 * @param width
 * @param height
 */
const hel_video_copy_video_frame_to_system = (
  x: number,
  y: number,
  width: number,
  height: number
) => {
  const w = width != null ? width : 0;
  const h = height != null ? height : 0;

  const findVideoJson = (json: any) => {
    for (const item of json.items) {
      if (item.clickablenodes != null) {
        for (const node of item.clickablenodes) {
          if (node.eventmap != null) {
            return node;
          }
        }
      }
    }
    return undefined;
  };

  const sceneJsonPath = getSceneJsonPath();
  if (sceneJsonPath === undefined) {
    console.error('sceneJsonPath not found');
    return;
  }
  fetch(sceneJsonPath).then((response) => {
    response.json().then((json) => {
      setSceneJson(json);
      const node = findVideoJson(json);
      if (node != null) {
        setVideoInfo(node);
        hel_setText(ETextParamType_CopyVideoFrameToSystem, `${x},${y},${w},${h}`);
      } else {
        console.error('The video info not found in the scene json file.');
      }
    });
  });
};

/**
 * 未使用
 */
const hel_video_cancel_to_copy_video_frame_to_system = () => {
  const video = getVideo();
  if (video === undefined) {
    return;
  }

  hel_setText(ETextParamType_CancelToCopyVideoFrameToSystem, '');
};
